import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import classNames from 'classnames'
import { ControlledCheckbox, ControlledHiddenInput } from 'transfix-form/components'
import ControlledCheckboxGroupInput from 'transfix-form/components/ControlledCheckboxGroupInput'
import { Button, IAutocompleteOption, Spinner } from 'transfix-ui/components'
import { RadioGroup } from 'transfix-ui/components/RadioGroup'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { ShipmentIssueUpdate } from 'apps/marketplace/apiTypes/shipmentIssues'
import TagSubscribers, { SubscriberValueCreate } from '../../../shared/TagSubscribers'
import {
  IRoleOption,
  IUseAssigneeRoles,
  NO_DEFAULT_OWNER_OPTION,
} from '../../hooks/useAssigneeRoles'

const useRolesStyles = makeStyles(theme => ({
  // override radio group and checkbox styles to line up rows within roles section
  checkboxColumn: {
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
    },
    '& label': {
      flexGrow: '0 !important',
      // the actual checkbox
      '& > span:first-of-type': {
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
      },
      // the checkbox label
      '& div': {
        paddingTop: '0 !important',
      },
    },
    marginTop: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(3.75),
      '& label': {
        borderBottom: 'none',
      },
    },
  },
  otherSubsColumn: {
    marginTop: 0,
  },
  radioGroupColumn: {
    flexGrow: 1,
    '& > fieldset > div': {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
    },
  },
  assigneeAutosuggest: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    '& .text-input-large.text-input .text-input-container': {
      height: '32px !important',
    },
  },
  rolesLabelsContainer: {
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
  },
  emptyAssigneeInput: {
    '& div > label:last-of-type': {
      marginTop: theme.spacing(1),
    },
  },
}))

const EMPTY_ASSIGNEE = {
  id: 'empty_assignee',
  value: 'empty_assignee',
  label: '',
  type: '',
  text: '',
  disabled: true,
}

/**
 * Returns new array where each option's label is changed to the provided argument
 */
const changeLabel = (options: IRoleOption[], label: string) => {
  if (!options?.length) return []
  return options.map(opt => ({ ...opt, label }))
}

interface IRolesProps extends Partial<IUseAssigneeRoles> {}

/**
 * Roles (owners and subscribers) section of Issue Type Form
 */
const Roles = ({
  assigneeRolesOptions = [],
  otherAssignees = [],
  setOtherAssignees = () => {},
  isAssigneeRolesLoading,
}: IRolesProps) => {
  const [showAutosuggest, setShowAutosuggest] = useState<boolean>(true)

  const classes = useRolesStyles()
  const otherSubscriberGroupClasses = classNames(classes.checkboxColumn, classes.otherSubsColumn, {
    [classes.emptyAssigneeInput]: showAutosuggest,
  })
  const ownerGroupClasses = classNames(classes.radioGroupColumn, {
    [classes.emptyAssigneeInput]: showAutosuggest,
  })

  const { control, setValue } = useFormContext()
  const currentFormValues = useWatch({
    control,
    name: [
      'shipmentIssueTypeDefaultOwner',
      'shipmentIssueTypeDefaultSubscribers',
      'otherSubscribers',
      'otherOwner',
    ],
  })
  const {
    otherSubscribers,
    shipmentIssueTypeDefaultOwner,
    shipmentIssueTypeDefaultSubscribers,
  } = currentFormValues

  // Hook to set shipmentIssueTypeDefaultSubscribers when otherSubscribers array changes
  useEffect(() => {
    if (otherSubscribers?.length) {
      setValue(
        'shipmentIssueTypeDefaultSubscribers',
        otherSubscribers.map((sub: string) => {
          return otherAssignees.find(option => option.id === sub)
        })
      )
    } else {
      // If otherSubscribers is empty, clear shipmentIssueTypeDefaultSubscribers
      setValue('shipmentIssueTypeDefaultSubscribers', [])
    }
  }, [otherSubscribers, otherAssignees, setValue])

  /** list of all options (both assignee roles and other selected assignees) */
  const assigneeOptions = useMemo(() => {
    const allOptions = [...assigneeRolesOptions, ...otherAssignees]
    if (showAutosuggest) allOptions.push(EMPTY_ASSIGNEE)
    return allOptions
  }, [assigneeRolesOptions, otherAssignees, showAutosuggest])

  /** list of all options for owners, as well as the 'No default owner' option */
  const ownerOptions = useMemo(() => {
    return changeLabel([NO_DEFAULT_OWNER_OPTION, ...assigneeOptions], 'owner')
  }, [assigneeOptions])

  /** list of options for other subscribers, ie. assignees that are not an assignee role */
  const otherSubscriberOptions = useMemo(() => {
    return changeLabel(
      [...otherAssignees, ...(showAutosuggest ? [EMPTY_ASSIGNEE] : [])],
      'subscriber'
    )
  }, [otherAssignees, showAutosuggest])

  const updateAssigneesHandler = ({ subscribers }: ShipmentIssueUpdate) => {
    const subscribersOptions =
      subscribers?.map((subscriber: SubscriberValueCreate) => ({
        id: String(subscriber.id),
        value: String(subscriber.id),
        label: subscriber.text || '',
        type: subscriber.type,
        text: subscriber.text || '',
      })) || []
    setOtherAssignees(subscribersOptions)
    setShowAutosuggest(false)
  }

  return (
    <Box position='relative'>
      <Typography variant='body1'>Roles</Typography>
      {isAssigneeRolesLoading ? (
        <Spinner />
      ) : (
        <Box display='flex' gap={3}>
          <Box display='flex' flexDirection='column' flexBasis={225} flexGrow={1}>
            <Box
              className={classes.rolesLabelsContainer}
              display='flex'
              gap='3px'
              flexDirection='column'>
              <Typography key={NO_DEFAULT_OWNER_OPTION.id} variant='body2'>
                {NO_DEFAULT_OWNER_OPTION.label}
              </Typography>
              {assigneeOptions.map(role => {
                if (role.label && role.id !== EMPTY_ASSIGNEE.id) {
                  return (
                    <Typography key={role.id} variant='body2'>
                      {role.label}
                    </Typography>
                  )
                }
                return null
              })}
            </Box>

            <div>
              {showAutosuggest && (
                <TagSubscribers
                  autoAddOnChange
                  hideTags
                  placeholder='Some other role'
                  value={(otherAssignees as unknown) as IAutocompleteOption[]}
                  onUpdateSubscribers={updateAssigneesHandler}
                  OuterBoxProps={{
                    className: classes.assigneeAutosuggest,
                  }}
                />
              )}
              <Button
                noPadding
                onClick={() => setShowAutosuggest(true)}
                color='tertiary'
                size='small'>
                + add another role
              </Button>
            </div>
          </Box>

          {/* OWNER RADIO GROUP */}
          <div className={ownerGroupClasses}>
            <Controller
              name='shipmentIssueTypeDefaultOwner'
              control={control}
              defaultValue={shipmentIssueTypeDefaultOwner}
              render={({ onChange }) => (
                <RadioGroup
                  stacked
                  required={false}
                  label=''
                  name='owner'
                  onOptionChange={(e: ChangeEvent<HTMLInputElement>) => {
                    onChange(e.target.value)
                  }}
                  checked={shipmentIssueTypeDefaultOwner}
                  options={ownerOptions}
                />
              )}
            />
          </div>

          {/* SUBSCRIBERS CHECKBOXES */}
          <Box
            display='flex'
            flexDirection='column'
            flexGrow={1}
            rowGap='6px'
            className={classes.checkboxColumn}>
            {assigneeRolesOptions.map(({ id, type: roleType }) => (
              <ControlledCheckbox
                key={id}
                control={control}
                name={`${roleType}-${id}`}
                CheckboxProps={{
                  label: 'subscriber',
                }}
              />
            ))}
            <div className={otherSubscriberGroupClasses}>
              <ControlledCheckboxGroupInput
                name='otherSubscribers'
                control={control}
                options={otherSubscriberOptions}
                defaultValue={shipmentIssueTypeDefaultSubscribers.map(
                  (sub: { id: string }) => sub.id
                )}
              />
              <ControlledHiddenInput control={control} name='shipmentIssueTypeDefaultSubscribers' />
            </div>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default Roles
